import {isTagEntry, isClosingTimeRadioSpotEntry} from '@/mixins/identifiers.js';

/**
 * Bundle functionality around batch-timing/removing/hotrotating a
 * selection of Entries.
 *
 * Needs the parent to implement 'this.entries' as a computed property.
 */

/**
 * This object only acts as an interface, grouping stuff together.
 */
class EntrySelectionOverlay {
  constructor(vm) {
    this.vm = vm;

    // What the overlay is meant to trigger after confirmation.
    this.mode = '';
    this.allowedModes = [
      // SpotEntry
      'timing',
      'hotrotating',
      'overlayediting',
      // SpotEntry, TickerEntry, TagEntry
      'removing',
      // TagEntry
      'reactivating',
    ];

    // Where the overlay is being used from; will affect displayed text.
    this.view = 'decoder';
    this.allowedViews = ['content', 'decoder'];

    this.active = false;
    this.titleIcon = '';
    this.title = '';
  }

  start(mode) {
    this._checkMode(mode);

    this.mode = mode;
    this.active = true;

    this.refresh();
  }

  stop() {
    this.mode = '';
    this.active = false;

    this.refresh();
  }

  toggle(mode) {
    this._checkMode(mode);

    if (this.active) {
      const lastMode = this.mode;
      this.stop();
      if (mode !== lastMode) {
        this.start(mode);
      }
    } else {
      this.start(mode);
    }

    this.refresh();
  }

  _checkView(view) {
    if (!this.allowedViews.includes(view)) {
      throw 'Unsupported view: ' + view;
    }
  }

  _checkMode(mode) {
    if (!this.allowedModes.includes(mode)) {
      throw 'Unsupported mode: ' + mode;
    }
  }

  refresh() {
    this._checkView(this.view);

    if (this.mode === 'timing') {
      this.titleIcon = 'timer';
      this.title =
        this.view === 'decoder'
          ? this.vm.numSelectedEntriesToTime
          : this.vm.numSelectedDecodersToTime;
    } else if (this.mode === 'removing') {
      this.titleIcon = 'block';
      this.title =
        this.view === 'decoder'
          ? this.vm.numSelectedEntriesToRemove
          : this.vm.numSelectedDecodersToRemove;
    } else if (this.mode === 'reactivating') {
      this.titleIcon = 'play_arrow';
      this.title = this.vm.numSelectedEntriesToReactivate;
    } else if (this.mode === 'overlayediting') {
      this.titleIcon = 'edit';
      this.title = this.vm.numSelectedDecodersToOverlayEdit;
    } else if (this.mode === 'hotrotating') {
      this.titleIcon = 'star';
      this.title = this.vm.numSelectedDecodersToHotrotate;
    }
  }
}

var entrySelectionOverlay = {
  data() {
    return {
      entrySelectionOverlay: new EntrySelectionOverlay(this),
    };
  },
  computed: {
    selectedEntries() {
      if (!this.entries) {
        return [];
      }
      return this.entries.filter((entry) => entry.selected);
    },
    selectedTagEntries() {
      return this.selectedEntries.filter((entry) => isTagEntry(entry));
    },
    onlyClosingtimeRadioSpotEntriesSelected() {
      const closingtimeOnly =
        this.selectedEntries.filter((entry) =>
          isClosingTimeRadioSpotEntry(entry)
        ).length === this.selectedEntries.length;
      return this.selectedEntries.length > 0 && closingtimeOnly;
    },
    /** Return a string like '2/5'. */
    selectedOfOverallEntriesText() {
      return `${this.selectedEntries.length}/${this.entries.length}`;
    },
    numSelectedEntriesToTime() {
      return this.getNumSelectedString(
        'numEntriesToTime',
        this.selectedOfOverallEntriesText
      );
    },
    numSelectedDecodersToTime() {
      return this.getNumSelectedString(
        'numDecodersToTime',
        this.selectedOfOverallEntriesText
      );
    },
    numSelectedEntriesToRemove() {
      return this.getNumSelectedString(
        'numEntriesToDelete',
        this.selectedOfOverallEntriesText
      );
    },
    numSelectedEntriesToReactivate() {
      return this.getNumSelectedString(
        'numEntriesToReactivate',
        this.selectedOfOverallEntriesText
      );
    },
    numSelectedDecodersToRemove() {
      return this.getNumSelectedString(
        'numDecodersToDelete',
        this.selectedOfOverallEntriesText
      );
    },
    numSelectedDecodersToHotrotate() {
      return this.getNumSelectedString(
        'numDecodersToHotrotate',
        this.selectedOfOverallEntriesText
      );
    },
    numSelectedDecodersToOverlayEdit() {
      return this.getNumSelectedString(
        'numDecodersToOverlayEdit',
        this.selectedOfOverallEntriesText
      );
    },
  },
  watch: {
    /** Update dialog title etc. when selection changes. */
    selectedEntries(val) {
      this.entrySelectionOverlay.refresh();
    },
  },
  methods: {
    getNumSelectedString(templateName, replacement) {
      const template = this.$t(templateName);
      let str = template.replace('{{ num }}', replacement);
      // Ensure first letter is capitalized.
      str = str.charAt(0).toUpperCase() + str.slice(1);
      return str;
    },
  },
};

export default entrySelectionOverlay;



// WEBPACK FOOTER //
// ./src/mixins/entrySelectionOverlay.js